import React, { useRef, useState, useEffect, useImperativeHandle } from 'react';
import Grid from '@material-ui/core/Grid';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import { Wizard } from 'components/UI';
import Box from '@material-ui/core/Box';
import { uiActions } from 'state/modules/UI';
import {
  Rulesets,
  BusinessHours,
  CustomerGroupDetails,
  VehiclesGroupsPricing,
  VehiclesGroupsLocations,
  CustomerGroupActivation,
} from 'components/VehiclesGroups';
import useStyles from './CustomerGroupOnboarding.styled';
import { ReservationsActions } from 'state/modules/reservations';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { useConfirm } from 'hooks';

const CustomersGroupOnboarding = ({ forwardRef, mode, onSave }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const formRef = useRef(null);
  const classes = useStyles();
  const activationRef = useRef(null);
  const { core } = useSelector((state) => state.config?.settings || {});
  const hasSubscriptionsEnabled = core?.subscription?.enabled || false;
  const hasPricingRulesSetEnabled = core?.pricing?.pricing_rules_set || false;
  const [showSaveAndExitModal, setShowSaveAndExitModal] = useState(false);
  const [edit, setEdit] = useState(false);
  const [pricing, setPricing] = useState('');
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );

  const stepsList = [
    {
      key: 'details',
      label: i18n.t('common.details'),
    },
    {
      key: 'locations',
      label: i18n.t('common.locations'),
    },
    ...(hasPricingRulesSetEnabled
      ? [
          {
            key: 'pricing',
            label: i18n.t('common.pricing'),
          },
        ]
      : []),
    ...(hasSubscriptionsEnabled
      ? [
          {
            key: 'activation',
            label: i18n.t('common.activation'),
          },
        ]
      : [
          {
            key: 'bh',
            label: i18n.t('vehicle_groups.business_hours.title'),
          },
          ...(sharingType !== 'FFB'
            ? [
                {
                  key: 'rulesets',
                  label: i18n.t('vehicle_groups.reservations_rulesets'),
                },
              ]
            : []),
          {
            key: 'activation',
            label: i18n.t('common.activation'),
          },
        ]),
  ];
  const successMessage = i18n.t('vehicle_groups.vehicle_group_created');

  const { rules } = useSelector((state) => state.reservations);
  const {
    loading,
    rrsTimes,
    relatedRulesets,
    onboarding: { exitOnboarding, exitStep, position, lastStep, errors, steps },
  } = useSelector((state) => state.vehiclesGroups);

  useEffect(() => {
    onSave(false);
  }, [position, onSave]);

  /**
   * Handle next step
   * Navigate to next step
   */
  const handleNextPosition = () => {
    dispatch(VehiclesGroupsActions.changePosition('next', null));
  };

  /**
   * Handle previous step
   * Navigate to previous step
   */
  const onPrevious = (pos) => {
    dispatch(VehiclesGroupsActions.changePosition('prev', null));
  };

  /**
   * Handle cancel/exit onboarding
   * Navigate to vehicles groups list
   * Reset onboarfing steps
   */
  const handleEndOnboarding = (showFeedback = true) => {
    dispatch(VehiclesGroupsActions.resetOnboarding({ type: 'reset' }));
    if (showFeedback) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: successMessage,
        })
      );
    }
    history.push(buildUrl('/service-groups'));
  };

  /**
   * Handle next step of the onboarding
   * Validate current step
   */
  const handleNextStep = (exit = false) => {
    switch (position) {
      case 0:
        if (formRef?.current) {
          return formRef.current.submitForm();
        }
        break;
      case 1:
        if (!steps?.locations?.assigned?.list || steps?.locations?.assigned?.list?.length === 0) {
          // This timeout is to avoid the conflicts with the snackbar when closing save and exit modal
          setTimeout(() => {
            dispatch(
              uiActions.handleShowSnackbar({
                type: 'error',
                message: i18n.t('vehicle_groups.add_location_vehicle_group'),
              })
            );
          }, 10);
        } else if (exit) {
          handleEndOnboarding(exit);
        } else {
          handleNextPosition();
        }
        break;
      case 2:
        if (exit) {
          handleEndOnboarding(exit);
        } else {
          handleNextPosition();
        }
        break;
      case 3:
        if (activationRef?.current) {
          activationRef.current.submitActivation();
          break;
        }
        if (!hasSubscriptionsEnabled) {
          if (exit) {
            handleEndOnboarding(exit);
          } else {
            handleNextPosition();
          }
        } else {
          activationRef.current.submitActivation();
        }
        break;
      case 4:
        if (activationRef?.current) {
          activationRef.current.submitActivation();
          break;
        }
        if (exit) {
          handleEndOnboarding(exit);
        } else {
          handleNextPosition();
        }
        break;
      case 5:
        if (activationRef?.current) {
          activationRef.current.submitActivation();
        }
        break;
      default:
        break;
    }
  };

  const displaySuccessModal = () => {
    handleEndOnboarding();
  };

  const onSaveAndExit = () => {
    handleNextStep(true);
  };

  const handleExit = (exit) => {
    dispatch(VehiclesGroupsActions.setExit(exit || !exitStep, 'exitStep'));
  };

  const handleShowOnSaveAndExitModal = () => {
    setShowSaveAndExitModal((prevState) => !prevState);
  };

  const handleOnSaveAndExit = () => {
    handleExit(true);
    setShowSaveAndExitModal((prevState) => !prevState);
  };

  useImperativeHandle(forwardRef, () => ({
    handleOnSaveAndExit,
  }));

  useEffect(() => {
    if (exitOnboarding) {
      handleEndOnboarding();
    }
  }, [exitOnboarding]); // eslint-disable-line

  useEffect(() => {
    if (
      errors &&
      lastStep &&
      (Array.isArray(errors) ||
        Object.keys(errors).length !== 0 ||
        Array.isArray(steps?.details?.activation_requirements))
    ) {
      handleEndOnboarding();
    } else if (lastStep) {
      displaySuccessModal();
    }
  }, [lastStep]); // eslint-disable-line

  useEffect(() => {
    if (!hasSubscriptionsEnabled) {
      dispatch(ReservationsActions.fetchReservationRules({ is_enabled: true }));
    }
  }, [dispatch]); // eslint-disable-line

  /**
   * Set wizard title
   * Validate if vg has been created
   */
  const setTitle = () => {
    let title = '';
    if (steps?.details) {
      title = i18n.t('vehicle_groups.wizard_title_edit_mode', {
        name: steps.details.name,
        id: steps.details.id,
      });
    } else {
      title = i18n.t('vehicle_groups.wizard_title_create_mode');
    }

    return title;
  };

  const [toggleNext, NextConfirm] = useConfirm({
    message: i18n.t('vehicle_groups.unsaved_changes'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.continue'),
    onConfirm: handleNextStep,
  });

  const showUnsavedPricing =
    stepsList[position]?.key === 'pricing' &&
    pricing != '' &&
    steps?.pricing?.currentRuleset?.rule_set?.id != pricing;

  return (
    <Box>
      <NextConfirm />
      <Wizard
        mode={mode}
        exit={exitStep}
        steps={stepsList}
        loading={loading}
        title={setTitle()}
        position={position}
        handleExit={handleExit}
        onNext={showUnsavedPricing ? toggleNext : handleNextStep}
        onPrevious={onPrevious}
        onSaveAndExit={onSaveAndExit}
        successMessage={successMessage}
        customContentClass={position === 1 ? undefined : classes.content}
        handleEndOnboarding={handleEndOnboarding}
        showSaveAndExitModal={showSaveAndExitModal}
        customStepperClass={classes.stepperContainer}
        handleShowOnSaveAndExitModal={handleShowOnSaveAndExitModal}
      >
        {stepsList[position]?.key === 'details' && (
          <CustomerGroupDetails
            rules={rules}
            exit={exitStep}
            errors={errors}
            onSave={onSave}
            formRef={formRef}
            step={steps.details}
            handleNextPosition={handleNextPosition}
          />
        )}
        {stepsList[position]?.key === 'locations' && (
          <Grid container direction="row" alignItems="center" justifyContent="center">
            <VehiclesGroupsLocations
              type="customers"
              loading={loading}
              step={steps.locations}
              currentGroup={steps.details}
              parentGroup={steps?.details?.parent?.id}
            />
          </Grid>
        )}
        {stepsList[position]?.key === 'pricing' && (
          <VehiclesGroupsPricing
            loading={loading}
            step={steps?.pricing}
            onSave={onSave}
            currentGroup={steps.details}
            pricingRulesets={relatedRulesets}
            edit={edit}
            setEdit={setEdit}
            pricing={pricing}
            setPricing={setPricing}
          />
        )}
        {stepsList[position]?.key === 'bh' && (
          <BusinessHours
            loading={loading}
            step={steps?.pricing}
            onSave={onSave}
            currentGroup={steps.details}
            pricingRulesets={relatedRulesets}
          />
        )}
        {stepsList[position]?.key === 'rulesets' && (
          <Rulesets
            step={rrsTimes}
            onSave={onSave}
            loading={loading}
            currentGroup={steps.details}
          />
        )}
        {stepsList[position]?.key === 'activation' && (
          <CustomerGroupActivation
            loading={loading}
            forwardRef={activationRef}
            onSave={onSave}
            currentGroup={steps.details}
            displaySuccessModal={displaySuccessModal}
            driverAccess={steps?.details?.driver_access}
          />
        )}
      </Wizard>
    </Box>
  );
};

export default CustomersGroupOnboarding;
